<template>
  <div
    class="speech-input"
    :class="{
      expanded: active,
      ending: phase === 'recording' && remaining <= 10
    }"
  >
    <button
      v-if="!active"
      type="button"
      class="speech-button"
      :disabled="disabled"
      :aria-label="copy.start || '语音输入'"
      :title="unavailable || copy.start || '语音输入'"
      @click="start"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="9" y="2" width="6" height="13" rx="3" />
        <path d="M5 10v2a7 7 0 0 0 14 0v-2M12 19v3M8 22h8" />
      </svg>
    </button>
    <template v-else>
      <div class="speech-display">
        <span v-if="phase === 'ready'" class="speech-ready" role="status">{{
          copy.limitReached || copy.finish
        }}</span>
        <div
          v-else-if="['recording', 'stopping'].includes(phase)"
          class="speech-wave"
          ref="wave"
          aria-hidden="true"
        >
          <i
            v-for="(level, index) in levels"
            :key="index"
            :style="{ height: 4 + level * 27 + 'px' }"
          />
        </div>
        <span v-else class="speech-progress">{{
          phase === "recognizing" ? copy.recognizing : "正在开启麦克风…"
        }}</span>
        <span v-if="['recording', 'stopping'].includes(phase)" class="speech-time" role="timer">{{
          phase === "recording" ? (remaining <= 10 ? countdown : elapsedText) : "1:00"
        }}</span>
        <span class="speech-sr" role="status">{{
          phase === "recording" && remaining <= 10 ? "录音即将结束" : ""
        }}</span>
      </div>
      <button
        v-if="phase !== 'recognizing'"
        type="button"
        class="speech-button"
        :aria-label="copy.cancel"
        @click="cancel"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="m6 6 12 12M18 6 6 18" />
        </svg>
      </button>
      <button
        v-if="['recording', 'stopping', 'ready'].includes(phase)"
        type="button"
        class="speech-button"
        :aria-label="copy.finish"
        :disabled="phase === 'stopping'"
        @click="finish"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="m4 12 5 5L20 5" />
        </svg>
      </button>
    </template>
  </div>
</template>
<script>
import { SpeechRecorder } from "./audio";
import { speechClient } from "./api";
import { requestId } from "@/network/session-identity";
export default {
  props: { disabled: Boolean },
  data: () => ({
    phase: "idle",
    remaining: 60,
    levels: Array(32).fill(0),
    copy: {},
    unavailable: "",
    alive: true
  }),
  computed: {
    active() {
      return this.phase !== "idle";
    },
    countdown() {
      return (this.copy.remaining || "剩余{seconds}秒").replace(
        "{seconds}",
        this.remaining
      );
    },
    elapsedText() {
      return "0:" + String(60 - this.remaining).padStart(2, "0");
    }
  },
  watch: {
    '$store.state.token'(token) {
      if (!token) { this.cancel(); this.client.close(); this.hiddenNotice = false; }
    },
    phase(value) {
      if (this.waveObserver) this.waveObserver.disconnect();
      if (value === "recording") this.$nextTick(() => {
        if (!this.alive || this.phase !== "recording") return;
        this.resizeWave();
        if (typeof ResizeObserver !== "undefined") {
          if (!this.waveObserver) this.waveObserver = new ResizeObserver(this.resizeWave);
          this.waveObserver.observe(this.$refs.wave);
        }
      });
    },
    active(value) {
      this.$emit("active", value);
    },
    disabled(value) {
      if (value && this.phase !== "idle") this.cancel();
    }
  },
  created() {
    this.client = speechClient();
  },
  mounted() {
    window.addEventListener("resize", this.resizeWave);
    this.onHidden = () => {
      if (document.hidden && this.phase !== "idle") {
        this.cancel();
        this.hiddenNotice = true;
      } else if (!document.hidden && this.hiddenNotice) {
        this.hiddenNotice = false;
        this.$emit("notice", "录音已结束，原文字草稿已保留。", true);
      }
    };
    document.addEventListener("visibilitychange", this.onHidden);
  },
  beforeDestroy() {
    this.alive = false;
    if (this.waveObserver) this.waveObserver.disconnect();
    window.removeEventListener("resize", this.resizeWave);
    this.cancel();
    this.client.close();
    document.removeEventListener("visibilitychange", this.onHidden);
  },
  methods: {
    resizeWave() {
      const wave = this.$refs.wave;
      if (!wave || this.phase !== "recording") return;
      const count = Math.max(1, Math.min(256, Math.floor(wave.clientWidth / 8)));
      if (count !== this.levels.length)
        this.levels = Array(Math.max(0, count - this.levels.length)).fill(0)
          .concat(this.levels).slice(-count);
    },
    async start() {
      if (this.disabled || this.active) return;
      const generation = (this.generation = (this.generation || 0) + 1);
      this.phase = "opening";
      this.$emit("notice", "");
      try {
        const info = await this.client.post("availability");
        if (!this.alive || generation !== this.generation) return;
        this.copy = info.copy;
        this.unavailable = info.displayMessage;
        if (!info.enabled) throw new Error(info.displayMessage);
        if (!window.isSecureContext)
          throw new Error("当前页面不支持录音，请通过HTTPS访问。");
        if (!navigator.mediaDevices || !window.AudioWorkletNode)
          throw new Error("当前浏览器暂不支持录音，请更新或更换浏览器。");
        this.$emit("capture");
        this.recorder = new SpeechRecorder(level => {
          this.levels = [...this.levels.slice(1), level];
        });
        await this.recorder.start();
        if (!this.alive || generation !== this.generation) return;
        this.phase = "recording";
        this.remaining = 60;
        this.started = performance.now();
        this.timer = setInterval(() => {
          this.remaining = Math.max(
            0,
            Math.ceil(60 - (performance.now() - this.started) / 1000)
          );
          if (!this.remaining) this.stopAtLimit();
        }, 100);
      } catch (e) {
        if (generation === this.generation) {
          this.cancel();
          if (e.silent) return;
          this.$emit(
            "notice",
            {
              NotAllowedError: "麦克风权限未开启，请允许录音后重试。",
              NotFoundError: "未找到麦克风，请连接设备后重试。",
              NotReadableError: "麦克风无法使用，请检查是否被其他应用占用。"
            }[e.name] ||
              (e.name === "Error" && e.message) ||
              "无法启动录音，请重新尝试或使用文字输入。",
            true
          );
        }
      }
    },
    async stopAtLimit() {
      if (this.phase !== "recording") return;
      const generation = this.generation;
      this.phase = "stopping";
      clearInterval(this.timer);
      try {
        const wav = await this.recorder.stop();
        if (!this.alive || generation !== this.generation) return;
        this.pendingAudio = wav;
        this.recorder = null;
        this.phase = "ready";
      } catch (e) {
        if (this.alive && generation === this.generation) {
          this.cancel();
          if (!e.silent) this.$emit("notice", "录音处理失败，请重新录音。", true);
        }
      }
    },
    cancel() {
      this.generation = (this.generation || 0) + 1;
      clearInterval(this.timer);
      if (this.recorder) this.recorder.close();
      this.recorder = null;
      this.pendingAudio = null;
      this.phase = "idle";
      this.levels = Array(32).fill(0);
    },
    async finish() {
      if (this.phase !== "recording" && this.phase !== "ready") return;
      const generation = this.generation;
      const pendingAudio = this.pendingAudio;
      this.pendingAudio = null;
      this.phase = "recognizing";
      clearInterval(this.timer);
      const id = requestId();
      try {
        let wav;
        try { wav = pendingAudio || await this.recorder.stop(); }
        catch (_) { throw new Error("录音处理失败，请重新录音。"); }
        if (!this.alive || generation !== this.generation) return;
        let result;
        try {
          result = await this.client.post("recognize", wav, id);
        } catch (e) {
          // Never resend audio automatically after an ambiguous HTTP result.
          if (e.definitive) throw e;
          result = await this.client.post("result", { requestId: id });
        }
        for (
          let attempt = 0;
          result.state === "PENDING" && attempt < 25;
          attempt++
        ) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          if (!this.alive || generation !== this.generation) return;
          result = await this.client.post("result", { requestId: id });
        }
        if (!this.alive || generation !== this.generation) return;
        if (result.state !== "SUCCEEDED")
          throw new Error(result.displayMessage);
        this.$emit("recognized", result);
      } catch (e) {
        if (this.alive && generation === this.generation && !e.silent)
          this.$emit("notice", e.message || "语音识别未成功，请重新尝试或使用文字输入。", true);
      } finally {
        if (generation === this.generation) this.cancel();
      }
    }
  }
};
</script>
<style scoped>
.speech-input {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: #526075;
}
.speech-input.expanded {
  flex: 1;
  min-width: 0;
  gap: 10px;
}
.speech-button {
  background: none;
  border: 0;
  border-radius: 50%;
  width: 38px;
  height: 38px;
  box-sizing: border-box;
  flex-shrink: 0;
  padding: 9px;
  cursor: pointer;
  color: inherit;
  display: grid;
  place-items: center;
}
.speech-button:hover {
  background: #edf1f7;
}
.speech-button:focus-visible {
  outline: 2px solid #839bc4;
  outline-offset: 2px;
}
.speech-button:disabled {
  opacity: 0.4;
  cursor: default;
}
.speech-button svg {
  width: 23px;
  height: 23px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.speech-display {
  display: flex;
  gap: 12px;
  align-items: center;
  min-width: 0;
  flex: 1;
}
.speech-wave {
  height: 36px;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 3px;
  flex: 1;
  overflow: hidden;
}
.speech-wave i {
  display: block;
  flex: 1;
  min-width: 0;
  border-radius: 4px;
  background: currentColor;
  transition: height 0.12s ease, color 0.3s;
}
.speech-time {
  font-size: 12px;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  color: #778397;
}
.ending {
  color: #b7792a;
}
.ending .speech-time {
  color: #a86518;
}
.speech-ready {
  display: flex;
  align-items: center;
  min-height: 36px;
  min-width: 0;
  color: #778397;
  font-size: 14px;
  line-height: 18px;
  text-align: left;
}
.speech-progress {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
  animation: speech-breathe 1.8s ease-in-out infinite;
}
.speech-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
}
@keyframes speech-breathe {
  50% {
    opacity: 0.45;
  }
}
@media (prefers-reduced-motion: reduce) {
  .speech-wave i {
    transition: none;
  }
  .speech-progress {
    animation: none;
  }
}
</style>
